
import { useState, useEffect } from "react";
import { MdClose, MdCheck } from "react-icons/md";
import { CircularProgress } from "@mui/material";
import tw from "tailwind-styled-components";
import { ActionSection, Label, ModalHeader, ScrollableContent, SideCancelBtn, SubmitBtn } from "./Styles/InputStyles";
import { GoSearch } from "react-icons/go";
import { capitalizeFirstLetter } from "../helpers";

export default function SelectMultipleStaffModal({
    isOpen,
    onClose,
    onSelect,
    options = [],
    selectedItem = [],
    title = "Select Items",
    searchPlaceholder = "Search...",
    emptyMessage = "No items available",
    noResultsMessage = "No items found",
    renderItem = null,
    idKey = "id",
    nameKey = "name",
    descriptionKeys = [],
    enableSearch = true,
    isLoading = false,
}) {
    const [tempSelectedItems, setTempSelectedItems] = useState(Array.isArray(selectedItem) ? selectedItem : []);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        if (isOpen) {
            setTempSelectedItems(Array.isArray(selectedItem) ? selectedItem : []);
            setSearchText("");
        }
    }, [isOpen, selectedItem]);

    const handleItemSelect = (item) => {
        const isAlreadySelected = tempSelectedItems.some(selected => selected[idKey] === item[idKey]);
        let newSelectedItems;

        if (isAlreadySelected) {
            newSelectedItems = tempSelectedItems.filter(selected => selected[idKey] !== item[idKey]);
        } else {
            newSelectedItems = [...tempSelectedItems, item];
        }

        setTempSelectedItems(newSelectedItems);
    };

    const handleSelectAll = () => {
        const allRooms = filteredOptions;

        if (areAllItemsSelected()) {
            setTempSelectedItems([]);
        } else {
            const allRoomIds = new Set(allRooms.map(item => item[idKey]));
            const currentlySelectedIds = new Set(tempSelectedItems.map(item => item[idKey]));

            const itemsToAdd = allRooms.filter(item => !currentlySelectedIds.has(item[idKey]));
            const newSelectedItems = [...tempSelectedItems, ...itemsToAdd];

            setTempSelectedItems(newSelectedItems);
        }
    };

    const handleApplySelection = () => {
        onSelect(tempSelectedItems);
        onClose();
    };

    const filteredOptions = options.filter(item =>
        item[nameKey]?.toLowerCase().includes(searchText.toLowerCase()) ||
        descriptionKeys.some(key => {
            if (Array.isArray(key)) {
                return key.some(k => item[k]?.toLowerCase().includes(searchText.toLowerCase()))
            }
            return item[key]?.toLowerCase().includes(searchText.toLowerCase())
        })
    );

    const getDescriptionValue = (item, key) => {
        if (Array.isArray(key)) {
            for (const k of key) {
                if (item[k]) return item[k];
            }
            return null;
        }
        return item[key];
    };

    const defaultRenderItem = (item, isSelected) => {
        const descriptionValues = descriptionKeys
            .map(key => getDescriptionValue(item, key))
            .filter(value => value != null && value !== "");

        const tenantCount = item.tenantCount || 0;

        return (
            <div className="flex items-center flex-1">
                <div className={`w-5 h-5 border-2 rounded mr-3 flex items-center justify-center ${isSelected
                    ? 'bg-primary-color border-primary-color'
                    : 'border-gray-300'
                    }`}>
                    {isSelected && <MdCheck className="text-white" size={14} />}
                </div>

                <div className="flex flex-col flex-1">
                    <div className="flex items-center gap-2">
                        <Label className="font-medium text-sm">
                            {capitalizeFirstLetter(item[nameKey])}
                        </Label>

                    </div>
                    {descriptionValues.length > 0 && (
                        <div className="text-xs mt-1 text-gray-500">
                            {descriptionValues.map((value, index) => (
                                <span key={index}>
                                    {value}
                                    {index < descriptionValues.length - 1 && " • "}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const isSelected = (itemId) => {
        return tempSelectedItems.some(item => item[idKey] === itemId);
    };

    const areAllItemsSelected = () => {
        if (filteredOptions.length === 0) return false;
        return filteredOptions.every(item => isSelected(item[idKey]));
    };

    const areSomeItemsSelected = () => {
        if (filteredOptions.length === 0) return false;
        return filteredOptions.some(item => isSelected(item[idKey]));
    };

    const countTotalRooms = () => {
        return filteredOptions.length;
    };

    const countSelectedRooms = () => {
        return tempSelectedItems.length;
    };

    if (!isOpen) return null;

    return (
        <Overlay>
            <ModalWrapper>
                <ModalHeader>
                    <h1 className="text-white text-lg font-medium">{title}</h1>
                    <button onClick={onClose}>
                        <MdClose color="white" size={24} />
                    </button>
                </ModalHeader>

                {enableSearch && (
                    <div className="p-4 relative">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder={searchPlaceholder}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                className="w-full px-8 h-12 py-2 border border-gray-200 rounded-md focus:outline-none focus:border-primary-color text-gray-800 placeholder-gray-300"
                            />
                            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                <GoSearch className="w-4 h-4 text-gray-400" />
                            </div>
                        </div>
                    </div>
                )}

                <ScrollableContent>
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <CircularProgress />
                        </div>
                    ) : filteredOptions.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            {options.length === 0 ? emptyMessage : noResultsMessage}
                        </div>
                    ) : (
                        <div className="px-4 space-y-2">
                            {filteredOptions.map((item) => {
                                const itemSelected = isSelected(item[idKey]);

                                return (
                                    <OptionRow
                                        key={item[idKey]}
                                        onClick={() => handleItemSelect(item)}
                                        selected={itemSelected}
                                    >
                                        {renderItem ?
                                            renderItem(item, itemSelected) :
                                            defaultRenderItem(item, itemSelected)
                                        }
                                    </OptionRow>
                                );
                            })}
                        </div>
                    )}
                </ScrollableContent>

                <ActionSection>
                    <SideCancelBtn
                        type="button"
                        onClick={() => {
                            onClose();
                        }}
                    >
                        Cancel
                    </SideCancelBtn>

                    <SubmitBtn
                        type="button"
                        onClick={handleApplySelection}
                        disabled={tempSelectedItems.length === 0}
                    >
                        Apply ({countSelectedRooms()})
                    </SubmitBtn>
                </ActionSection>
            </ModalWrapper>
        </Overlay>
    );
}

const Overlay = tw.div`fixed inset-0 z-50 flex items-center justify-end bg-black/50`;
const ModalWrapper = tw.div`bg-white w-11/12 h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;
const OptionRow = tw.div`
    flex items-center justify-between p-4 border border-gray-200 rounded-lg cursor-pointer
    ${props => props.selected
        ? 'border-primary-color bg-blue-50 hover:bg-blue-100'
        : 'hover:bg-gray-50'
    }
`;